/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { TradeRequest, TradeResult } from '../../types/trading';
import { createRaydiumPool } from '../../lib/solana/raydium';
import { bondingCurveDB } from '../../lib/bonding-curve-db';
import { BONDING_CURVE_TARGET_SOL, BONDING_CURVE_TARGET_SUI } from '../../lib/constants';
import { calculateBuyOutput, calculateSellOutput, checkGraduationThreshold, validateBotProtection } from '../../lib/bonding-curve/helpers';
import { connection } from '../../lib/solana/solana';

/**
 * Simulates the HotDEX On-Chain Program (AMM Logic & Bonding Curve)
 */
export class HotDEX {
  private static GRADUATION_FEE_PERCENT = 0.05; // 5% Fee on graduation
  
  /**
   * Simulates a swap on the bonding curve using the DB state
   */
  static async executeSwap(request: TradeRequest, currentPrice: number): Promise<TradeResult> {
    console.log(`[HotDEX] Processing ${request.side} for ${request.amountIn} ${request.chain}...`);

    // 1. Network Health Check (Integration with Failover RPC)
    // We try to fetch the current slot. If this fails, the trade fails (simulating network down).
    let currentBlock = 0;
    try {
        currentBlock = await connection.getSlot();
        console.log(`[HotDEX] Validated Network Connection at Slot: ${currentBlock}`);
    } catch (e) {
        throw new Error("Network unreachable. Please check your RPC connection.");
    }

    // 2. Fetch State
    const curve = bondingCurveDB.getCurve(request.tokenAddress);
    const state = curve || bondingCurveDB.initializeCurve(request.tokenAddress, request.chain);

    // 3. Bot Protection & Validation
    const securityCheck = validateBotProtection(state, request.amountIn, request.side, request.walletAddress);
    if (!securityCheck.allowed) {
        console.warn(`[HotDEX] Security Block: ${securityCheck.reason}`);
        throw new Error(securityCheck.reason || "Transaction blocked by anti-snipe protection.");
    }

    // 4. Bonding Curve Math (Constant Product x * y = k)
    let amountOut = 0;
    let newVirtualSol = state.virtualSolReserves;
    let newVirtualTokens = state.virtualTokenReserves;
    let fee = 0;
    let tokenTax = 0;
    let priceImpact = 0;

    if (request.side === 'BUY') {
        const result = calculateBuyOutput(state, request.amountIn);
        amountOut = result.amountOut;
        fee = result.fee;
        priceImpact = result.priceImpact;

        // Update State Variables
        newVirtualSol = state.virtualSolReserves + (request.amountIn - fee);
        newVirtualTokens = state.virtualTokenReserves - amountOut;

        // Apply Token Transaction Tax (if any) on Output
        if (request.transferFeeBasisPoints && request.transferFeeBasisPoints > 0) {
            tokenTax = amountOut * (request.transferFeeBasisPoints / 10000);
            amountOut = amountOut - tokenTax;
            console.log(`[HotDEX] Deducted Token Tax: ${tokenTax} (${request.transferFeeBasisPoints}bps)`);
        }

    } else {
        // Sell
        let netTokenIn = request.amountIn;
        if (request.transferFeeBasisPoints && request.transferFeeBasisPoints > 0) {
            tokenTax = request.amountIn * (request.transferFeeBasisPoints / 10000);
            netTokenIn = request.amountIn - tokenTax;
            console.log(`[HotDEX] Deducted Token Tax: ${tokenTax} (${request.transferFeeBasisPoints}bps)`);
        }

        const result = calculateSellOutput(state, netTokenIn);
        amountOut = result.amountOut;
        fee = result.fee;
        priceImpact = result.priceImpact;

        // Update State Variables
        newVirtualTokens = state.virtualTokenReserves + netTokenIn;
        newVirtualSol = state.virtualSolReserves - (amountOut + fee);
    }

    // 5. Update Price & Metrics
    const newPrice = newVirtualSol / newVirtualTokens;
    const marketCap = newPrice * 1_000_000_000; // Fixed supply
    
    // Calculate Progress
    const target = request.chain === 'SOL' ? BONDING_CURVE_TARGET_SOL : BONDING_CURVE_TARGET_SUI;
    const currentColl = newVirtualSol - (request.chain === 'SOL' ? 30 : 1500); // Minus virtual start
    const progress = Math.min((currentColl / target) * 100, 100);

    // 6. Update DB State (Optimistic)
    const updatedState = {
        ...state,
        virtualSolReserves: newVirtualSol,
        virtualTokenReserves: newVirtualTokens,
        realSolReserves: currentColl,
        tokenPrice: newPrice,
        marketCap,
        progress
    };
    
    bondingCurveDB.updateCurve(request.tokenAddress, updatedState);

    // 7. Generate Hash
    const chars = '0123456789abcdef';
    let txHash = '5'; // Solana sig start
    for (let i = 0; i < 87; i++) txHash += chars[Math.floor(Math.random() * 16)];

    // 8. Check for Graduation
    let graduationData = undefined;
    if (checkGraduationThreshold(updatedState) && !state.isGraduated) {
        console.log(`[HotDEX] 🎓 Graduation Threshold Reached! Migration initiated...`);
        graduationData = await this.graduateToken(request.tokenAddress, request.chain, currentColl);
        
        bondingCurveDB.updateCurve(request.tokenAddress, {
            isGraduated: true,
            graduationTx: graduationData.txHash,
            poolAddress: graduationData.poolId
        });
    }

    return {
      txHash,
      amountOut,
      fee,
      tokenTax,
      priceImpact,
      newPrice,
      newMarketCap: marketCap,
      timestamp: Date.now(),
      status: 'success',
      blockNumber: currentBlock,
      graduated: !!graduationData,
      graduationDetails: graduationData
    };
  }

  /**
   * Handles the migration of liquidity to external DEX
   */
  private static async graduateToken(tokenMint: string, chain: 'SOL' | 'SUI', totalLiquidity: number) {
      // 1. Calculate Success Fee (5%)
      const serviceFee = totalLiquidity * this.GRADUATION_FEE_PERCENT;
      const liquidityToMigrate = totalLiquidity - serviceFee;

      console.log(`[Graduation] Total Liq: ${totalLiquidity}, Fee: ${serviceFee}, Migrating: ${liquidityToMigrate}`);

      // 2. Mock Pool Creation on External DEX
      let poolInfo;
      if (chain === 'SOL') {
          // Create Raydium Pool
          poolInfo = await createRaydiumPool(tokenMint, 100000000, liquidityToMigrate);
      } else {
          // Create Cetus Pool (Mock)
          poolInfo = { poolId: `CETUS_${Math.random().toString(36).slice(2)}`, txHash: '0x...' };
      }

      return {
          dex: chain === 'SOL' ? 'Raydium' as const : 'Cetus' as const,
          poolId: poolInfo.poolId,
          initialLiquidity: liquidityToMigrate,
          serviceFee
      };
  }
}